import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, X, Check, Search } from "lucide-react";

/**
 * Lightweight multi-select checkbox dropdown.
 * props:
 *   options: [{ value, label }]
 *   values: string[]  (selected values)
 *   onChange: (values) => void
 *   placeholder: "All …"
 *   testid: base test-id
 */
export default function MultiSelect({ options, values, onChange, placeholder = "Select…", testid = "ms" }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return options;
    return options.filter((o) => o.label.toLowerCase().includes(s));
  }, [q, options]);

  const toggle = (v) => {
    if (values.includes(v)) onChange(values.filter((x) => x !== v));
    else onChange([...values, v]);
  };
  const clear = (e) => { e.stopPropagation(); onChange([]); };
  const selectAll = () => onChange(filtered.map((o) => o.value));

  const label = values.length === 0
    ? placeholder
    : values.length === 1
      ? (options.find((o) => o.value === values[0])?.label || values[0])
      : `${values.length} selected`;

  return (
    <div ref={ref} className="relative" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="input-lg h-9 text-sm w-full flex items-center justify-between gap-2 text-left"
        data-testid={`${testid}-btn`}
      >
        <span className={`truncate ${values.length === 0 ? "text-gray-400" : ""}`}>{label}</span>
        <div className="flex items-center gap-1 shrink-0">
          {values.length > 0 && (
            <span onClick={clear}
              className="text-gray-400 hover:text-black p-0.5"
              data-testid={`${testid}-clear`}
              role="button">
              <X size={13} />
            </span>
          )}
          <ChevronDown size={13} className={`text-gray-500 transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </button>
      {open && (
        <div className="absolute z-50 mt-1 left-0 right-0 min-w-[220px] bg-white border border-gray-300 rounded-sm shadow-lg max-h-[280px] flex flex-col">
          <div className="p-2 border-b border-gray-100 flex items-center gap-2">
            <div className="relative flex-1">
              <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Search…"
                className="w-full h-8 pl-7 pr-2 border border-gray-200 rounded-sm text-xs"
                data-testid={`${testid}-search`}
                autoFocus
              />
            </div>
            <button type="button" onClick={selectAll}
              className="text-[10px] font-bold text-gray-500 hover:text-black tracking-wider uppercase"
              data-testid={`${testid}-selectall`}>
              Select all
            </button>
          </div>
          <div className="overflow-auto flex-1">
            {filtered.length === 0 && (
              <div className="p-4 text-center text-xs text-gray-400">Nothing matches</div>
            )}
            {filtered.map((o) => {
              const checked = values.includes(o.value);
              return (
                <button type="button" key={o.value}
                  onClick={() => toggle(o.value)}
                  className={`w-full flex items-center gap-2 px-2 py-1.5 text-xs text-left hover:bg-gray-50 ${checked ? "bg-gray-50" : ""}`}
                  data-testid={`${testid}-opt-${o.value}`}>
                  <span className={`w-4 h-4 border rounded-sm flex items-center justify-center ${checked ? "bg-black border-black text-white" : "border-gray-300"}`}>
                    {checked && <Check size={11} />}
                  </span>
                  <span className="truncate flex-1">{o.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
